import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { money } from "@/lib/api";
import { useI18n } from "@/context/PrefsContext";
import { StatCard, StatusBadge } from "@/components/ui-kit";
import {
  DollarSign, AlertCircle, CheckCircle2, Users, UserPlus, Receipt, Calendar, ArrowRight,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

export default function Dashboard() {
  const [data, setData] = useState(null);
  const { t } = useI18n();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return null;

  const stats = [
    { icon: DollarSign, label: "Total Revenue", value: money(data.total_revenue), accent: "#16a34a", testid: "stat-revenue" },
    { icon: AlertCircle, label: "Outstanding", value: money(data.outstanding), accent: "#ef4444", testid: "stat-outstanding" },
    { icon: CheckCircle2, label: "Jobs Completed", value: data.jobs_completed, accent: "#5A189A", testid: "stat-jobs" },
    { icon: Users, label: "Active Clients", value: data.active_clients, accent: "#7B2CBF", testid: "stat-clients" },
    { icon: UserPlus, label: "Open Leads", value: data.open_leads, accent: "#D4AF37", testid: "stat-leads" },
    { icon: Receipt, label: "Total Invoices", value: data.total_invoices, accent: "#0ea5e9", testid: "stat-invoices" },
  ];

  return (
    <div data-testid="dashboard-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-[#121212] tracking-tight">{t("Dashboard")}</h1>
        <p className="text-[#52525B] mt-1 text-sm">{t("Welcome back — here's what's happening at Magic Touch.")}</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        {stats.map((s) => <StatCard key={s.label} {...s} label={t(s.label)} />)}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Revenue chart */}
        <div className="mt-card p-6 lg:col-span-2">
          <h3 className="font-display text-xl font-semibold text-[#121212] mb-4">{t("Monthly Revenue")}</h3>
          {data.monthly_revenue.length === 0 ? (
            <div className="h-64 flex items-center justify-center text-[#9b8bb5] text-sm">
              {t("No revenue recorded yet. Completed jobs and payments will appear here.")}
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={data.monthly_revenue}>
                <defs>
                  <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#7B2CBF" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#7B2CBF" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0ebf7" vertical={false} />
                <XAxis dataKey="month" stroke="#9b8bb5" fontSize={12} />
                <YAxis stroke="#9b8bb5" fontSize={12} />
                <Tooltip formatter={(v) => money(v)} />
                <Area type="monotone" dataKey="revenue" stroke="#5A189A" strokeWidth={2.5} fill="url(#rev)" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Upcoming jobs */}
        <div className="mt-card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-xl font-semibold text-[#121212]">{t("Upcoming Jobs")}</h3>
            <button onClick={() => navigate("/schedule")} className="text-[#5A189A] text-sm font-medium flex items-center gap-1 hover:text-[#D4AF37]">
              {t("View")} <ArrowRight size={14} />
            </button>
          </div>
          {data.upcoming_jobs.length === 0 ? (
            <div className="text-[#9b8bb5] text-sm py-8 text-center">{t("No scheduled jobs.")}</div>
          ) : (
            <div className="space-y-3">
              {data.upcoming_jobs.map((j) => (
                <div key={j.id} className="flex items-center gap-3 p-3 rounded-xl bg-[#faf8fd]">
                  <div className="w-9 h-9 rounded-lg bg-[#5A189A]/10 text-[#5A189A] flex items-center justify-center">
                    <Calendar size={16} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-[#121212] truncate">{j.customer_name}</div>
                    <div className="text-xs text-[#52525B]">{j.service} · {j.date}</div>
                  </div>
                  <StatusBadge status={j.status} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Recent invoices */}
      <div className="mt-card p-6 mt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-semibold text-[#121212]">{t("Recent Invoices")}</h3>
          <button onClick={() => navigate("/invoices")} className="text-[#5A189A] text-sm font-medium flex items-center gap-1 hover:text-[#D4AF37]">
            {t("View all")} <ArrowRight size={14} />
          </button>
        </div>
        {data.recent_invoices.length === 0 ? (
          <div className="text-[#9b8bb5] text-sm py-8 text-center">{t("No invoices yet.")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-[#52525B] border-b border-[#efeaf6]">
                  <th className="py-2.5 pr-4">{t("Invoice")}</th>
                  <th className="py-2.5 pr-4">{t("Customer")}</th>
                  <th className="py-2.5 pr-4">{t("Total")}</th>
                  <th className="py-2.5 pr-4">{t("Balance")}</th>
                  <th className="py-2.5">{t("Status")}</th>
                </tr>
              </thead>
              <tbody>
                {data.recent_invoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-[#f4f0fa] hover:bg-[#faf8fd] cursor-pointer" onClick={() => navigate("/invoices")}>
                    <td className="py-3 pr-4 font-medium text-[#5A189A]">{inv.invoice_number}</td>
                    <td className="py-3 pr-4">{inv.customer_name}</td>
                    <td className="py-3 pr-4">{money(inv.total)}</td>
                    <td className="py-3 pr-4">{money(inv.balance_due)}</td>
                    <td className="py-3"><StatusBadge status={inv.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
